/**
 * THE one copy of the Your picks ranking and the For You rule. The app (Browse,
 * Favorites, onboarding) and the suggested-picks-grades function both import
 * this file, so the cards a guest sees and their grades can never disagree.
 * Only relative imports, so Deno and Vite both read it.
 */
import { classifyParking, PARKING_PARTIAL_MULTIPLIER } from "./importanceScale.ts";
import { getWifiGrade, getOutletsGrade, getSeatingGrade, getParkingGrade, type LetterGrade } from "./amenityGrades.ts";

export interface PicksAmenities {
  has_wifi?: boolean | null;
  wifi_speed_avg?: number | null;
  outlet_availability?: string | null;
  seating_capacity?: string | null;
  menu_type?: string | null;
  parking_available?: string | null;
}
export interface PicksCafe {
  id?: string;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  work_readiness_score?: number | null;
  amenities?: PicksAmenities | null;
}
// deno-lint-ignore no-explicit-any
export type PicksPrefs = Record<string, any>;
export interface SuggestionGeo { lat: number; lng: number; }

/** Candidate pool for Your picks: the first 25 by score, within 15 miles when a place is known. */
export const SUGGESTED_POOL_LIMIT = 25;
export const SUGGESTED_RADIUS_MILES = 15;
/** Picks prefer cafés at or above this score, relaxed when fewer than 3 qualify. */
export const SUGGESTED_MIN_WORK_SCORE = 60;
/**
 * With no place known ("Not now"), picks come from Southern California, where
 * nearly all Grounded cafés are: California cafés south of this latitude (the
 * Tehachapi line, so Santa Barbara, Kern and San Luis Obispo count).
 */
export const SOCAL_MAX_LATITUDE = 35.8;
/** For You: personalization at or above 70, within 50 miles of a known place. */
export const FOR_YOU_MIN_SCORE = 70;
export const FOR_YOU_MAX_MILES = 50;

/** Distance in miles between two points. */
export function distanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Personalization score 0 to 100 from the importance columns. A null column
 * means never answered and counts in neither numerator nor denominator.
 */
export function calculatePersonalizationScore(
  cafe: PicksCafe,
  preferences: PicksPrefs | null
): number {
  if (!preferences) return 0;

  let score = 0;
  let maxScore = 0;

  const answered = (v: number | null | undefined): number | null =>
    v === null || v === undefined ? null : v;

  // WiFi importance
  const wifiImportance = answered(preferences.wifi_importance);
  if (wifiImportance !== null) {
    maxScore += wifiImportance * 10;
    if (cafe.amenities?.has_wifi) {
      score += wifiImportance * 10;
      // Bonus for fast WiFi
      if (cafe.amenities.wifi_speed_avg && cafe.amenities.wifi_speed_avg > 50) {
        score += wifiImportance * 2;
      }
    }
  }

  // Outlet importance
  const outletImportance = answered(preferences.outlet_importance);
  if (outletImportance !== null) {
    maxScore += outletImportance * 10;
    if (cafe.amenities?.outlet_availability &&
        cafe.amenities.outlet_availability !== "none" &&
        cafe.amenities.outlet_availability !== "unknown") {
      score += outletImportance * 10;
      // Bonus for plenty of outlets
      if (cafe.amenities.outlet_availability === "plenty") {
        score += outletImportance * 2;
      }
    }
  }

  // Noise preference matching disabled - no reliable noise data yet

  // Seating capacity importance
  const seatingImportance = answered(preferences.seating_comfort_importance);
  if (seatingImportance !== null) {
    maxScore += seatingImportance * 10;
    const seatingCapacity = cafe.amenities?.seating_capacity;
    if (seatingCapacity === 'large') {
      score += seatingImportance * 10;
    } else if (seatingCapacity === 'medium') {
      score += seatingImportance * 6;
    } else if (seatingCapacity === 'small') {
      score += seatingImportance * 2;
    }
  }

  // Food importance
  const foodImportance = answered(preferences.food_importance);
  if (foodImportance !== null) {
    maxScore += foodImportance * 10;
    if (cafe.amenities?.menu_type) {
      if (cafe.amenities.menu_type === "full_food") {
        score += foodImportance * 10;
      } else if (cafe.amenities.menu_type === "pastries_and_drinks") {
        score += foodImportance * 5;
      }
    }
  }

  // Parking importance
  const parkingImportance = answered(preferences.parking_importance);
  if (parkingImportance !== null) {
    maxScore += parkingImportance * 10;
    switch (classifyParking(cafe.amenities?.parking_available)) {
      case "free":
        score += parkingImportance * 10;
        break;
      case "paid":
        score += parkingImportance * PARKING_PARTIAL_MULTIPLIER;
        break;
      case "none":
      case "unknown":
      default:
        // no credit
        break;
    }
  }


  // Calculate percentage score
  if (maxScore === 0) return 50; // Default middle score
  return Math.round((score / maxScore) * 100);
}

/** The For You tag rule, same for Browse and Your picks. No place known, no tag. */
export function isForYou(cafe: PicksCafe, prefs: PicksPrefs | null, geo: SuggestionGeo | null): boolean {
  if (!prefs || !geo || cafe.latitude == null || cafe.longitude == null) return false;
  if (distanceMiles(geo.lat, geo.lng, Number(cafe.latitude), Number(cafe.longitude)) > FOR_YOU_MAX_MILES) return false;
  return calculatePersonalizationScore(cafe, prefs) >= FOR_YOU_MIN_SCORE;
}

/** A California café south of SOCAL_MAX_LATITUDE. */
export function isSouthernCalifornia(cafe: PicksCafe): boolean {
  const state = typeof cafe.state === "string" ? cafe.state.trim().toUpperCase() : "";
  if (state !== "CA" && state !== "CALIFORNIA") return false;
  const lat = cafe.latitude == null ? NaN : Number(cafe.latitude);
  return Number.isFinite(lat) && lat <= SOCAL_MAX_LATITUDE;
}

/** The area a card shows: neighborhood, else city. */
function areaKey(cafe: PicksCafe): string {
  return String(cafe.neighborhood || cafe.city || "").trim().toLowerCase();
}

/**
 * Keeps the order but takes one café per area first, then fills any space
 * left in the same order, so a short list is never cut short.
 */
function spreadByArea<T extends PicksCafe>(ranked: T[], limit: number): T[] {
  const picked: T[] = [];
  const areas = new Set<string>();
  for (const c of ranked) {
    if (picked.length >= limit) break;
    const key = areaKey(c);
    if (key && areas.has(key)) continue;
    if (key) areas.add(key);
    picked.push(c);
  }
  for (const c of ranked) {
    if (picked.length >= limit) break;
    if (!picked.includes(c)) picked.push(c);
  }
  return picked;
}

/**
 * Gate on work score (>= 60), relax if fewer than 3 qualify, then sort
 * nearest first with personalization and work score tiebreakers.
 * With no place known: only Southern California cafés (when at least
 * `limit` are in the pool), best first, one per area.
 */
export function rankSuggestedCafes<T extends PicksCafe>(
  pool: T[],
  prefs: PicksPrefs | null,
  geo: SuggestionGeo | null,
  limit = 3,
): T[] {
  if (!pool || pool.length === 0) return [];
  let candidates = pool;
  if (!geo) {
    const socal = pool.filter(isSouthernCalifornia);
    if (socal.length >= limit) candidates = socal;
  }
  const quality = candidates.filter((c) => (c.work_readiness_score ?? 0) >= SUGGESTED_MIN_WORK_SCORE);
  const ranked = quality.length >= 3 ? quality : candidates;
  const distanceOf = (c: PicksCafe): number | null => {
    if (!geo || c.latitude == null || c.longitude == null) return null;
    return distanceMiles(geo.lat, geo.lng, Number(c.latitude), Number(c.longitude));
  };
  const sorted = [...ranked]
    .sort((a, b) => {
      const da = distanceOf(a);
      const db = distanceOf(b);
      if (da !== db) return (da ?? Infinity) - (db ?? Infinity);
      return (
        calculatePersonalizationScore(b, prefs ?? null) -
          calculatePersonalizationScore(a, prefs ?? null) ||
        (b.work_readiness_score ?? 0) - (a.work_readiness_score ?? 0)
      );
    });
  if (!geo) return spreadByArea(sorted, limit);
  return sorted.slice(0, limit);
}

// ── What a Your picks card carries: the four letters and the For You flag ──

export interface PickGrades { wifi: LetterGrade; outlets: LetterGrade; seating: LetterGrade; parking: LetterGrade; }

export function toPickGrades(amenities: Record<string, unknown> | null | undefined): PickGrades {
  // deno-lint-ignore no-explicit-any
  const a = (amenities ?? null) as any;
  return {
    wifi: getWifiGrade(a).grade,
    outlets: getOutletsGrade(a).grade,
    seating: getSeatingGrade(a).grade,
    parking: getParkingGrade(a).grade,
  };
}

/** Onboarding priority ids accepted from the picks screen. */
export const PICK_PRIORITY_IDS = ["wifi", "outlets", "seating", "parking"] as const;
